import { useEffect } from 'react';

interface MetaConfig {
  title: string;
  description: string;
  ogImage?: string;
  ogType?: string;
  url?: string;
}

/**
 * Hook to set meta tags for SEO on page load
 * Sets title, description, OG tags, and canonical URL
 * Should be called at the top of each page component
 */
export const useSetMeta = (config: MetaConfig) => {
  useEffect(() => {
    // Set page title
    document.title = config.title;

    // Update or create description meta tag
    let descMeta = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!descMeta) {
      descMeta = document.createElement('meta');
      descMeta.name = 'description';
      document.head.appendChild(descMeta);
    }
    descMeta.content = config.description;

    // Update OG title
    let ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement;
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = config.title;

    // Update OG description
    let ogDesc = document.querySelector('meta[property="og:description"]') as HTMLMetaElement;
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute('property', 'og:description');
      document.head.appendChild(ogDesc);
    }
    ogDesc.content = config.description;

    // Update OG image if provided
    if (config.ogImage) {
      let ogImage = document.querySelector('meta[property="og:image"]') as HTMLMetaElement;
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      ogImage.content = config.ogImage;
    }

    // Update OG type if provided
    if (config.ogType) {
      let ogType = document.querySelector('meta[property="og:type"]') as HTMLMetaElement;
      if (!ogType) {
        ogType = document.createElement('meta');
        ogType.setAttribute('property', 'og:type');
        document.head.appendChild(ogType);
      }
      ogType.content = config.ogType;
    }

    // Keep og:url in sync with the current page URL on every navigation
    let ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement;
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.content = config.url || window.location.href.split('?')[0].replace(/\/+$/, '') || 'https://zodiage.com/';
  }, [config.title, config.description, config.ogImage, config.ogType, config.url]);
};
